/**
 * usePromptStash — localStorage-backed stash for unsent draft prompts
 * ("git stash" for the chat input). State-backed (unlike usePromptHistory's
 * refs) so the stash menu re-renders on change. 50-entry cap, newest first.
 */
import { useState, useCallback } from 'react'

const STORAGE_PREFIX = 'papan-agent-prompt-stash-'
const MAX_ENTRIES = 50

export interface StashEntry {
  id: string
  text: string
  at: number
}

function load(workspaceId: string): StashEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + workspaceId)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter((e): e is StashEntry =>
      e && typeof e.id === 'string' && typeof e.text === 'string' && typeof e.at === 'number',
    ).slice(0, MAX_ENTRIES)
  } catch {
    return []
  }
}

function save(workspaceId: string, entries: StashEntry[]): void {
  try {
    localStorage.setItem(STORAGE_PREFIX + workspaceId, JSON.stringify(entries))
  } catch { /* ignore quota errors */ }
}

export function usePromptStash(workspaceId: string) {
  const [entries, setEntries] = useState<StashEntry[]>(() => load(workspaceId))

  const push = useCallback((text: string) => {
    const t = text.trim()
    if (!t) return
    setEntries(prev => {
      const next = [
        { id: `s_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, text: t, at: Date.now() },
        ...prev,
      ].slice(0, MAX_ENTRIES)
      save(workspaceId, next)
      return next
    })
  }, [workspaceId])

  const remove = useCallback((id: string) => {
    setEntries(prev => {
      const next = prev.filter(e => e.id !== id)
      save(workspaceId, next)
      return next
    })
  }, [workspaceId])

  return { entries, push, remove }
}
